/**
 * Helper functions to generate Schema.org structured data for SEO
 * These helpers create JSON-LD formatted data for search engines
 */

/**
 * Generate JobPosting structured data
 * @param {Object} job - Job data
 * @param {Object} company - Company data
 * @returns {Object} JSON-LD JobPosting schema
 */
export const generateJobPostingSchema = (job, company) => {
  if (!job || !company) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title || 'Remote Job',
    description: job.description || job.summary || '',
    datePosted: job.createdAt || job.publishedAt || new Date().toISOString(),
    hiringOrganization: {
      '@type': 'Organization',
      name: company.name || 'Company',
      sameAs: company.website || company.url,
      logo: company.logo || company.logoUrl
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job.location || 'Remote',
        addressCountry: job.country || 'Worldwide'
      }
    },
    employmentType: job.type || job.employmentType || 'FULL_TIME',
    jobLocationType: 'TELECOMMUTE',
    applicantLocationRequirements: {
      '@type': 'Country',
      name: job.regionalRestrictions || 'Worldwide'
    }
  };

  // Add valid through date if available
  if (job.expiresAt || job.validThrough) {
    schema.validThrough = job.expiresAt || job.validThrough;
  }

  // Add salary if available
  if (job.salary || job.salaryMin || job.salaryMax) {
    schema.baseSalary = {
      '@type': 'MonetaryAmount',
      currency: job.salaryCurrency || 'USD',
      value: {
        '@type': 'QuantitativeValue',
        minValue: job.salaryMin || job.salary,
        maxValue: job.salaryMax || job.salary,
        unitText: job.salaryPeriod || 'YEAR'
      }
    };
  }

  return schema;
};

/**
 * Generate Organization structured data
 * @param {Object} company - Company data
 * @returns {Object} JSON-LD Organization schema
 */
export const generateOrganizationSchema = (company) => {
  if (!company) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: company.name || '',
    description: company.description || company.about || '',
    url: company.website || company.url || '',
    logo: company.logo || company.logoUrl || '',
    foundingDate: company.foundedDate || company.founded
  };

  // Add social media links if available
  const socialLinks = [];
  if (company.twitter) socialLinks.push(company.twitter);
  if (company.linkedin) socialLinks.push(company.linkedin);
  if (company.facebook) socialLinks.push(company.facebook);
  if (company.github) socialLinks.push(company.github);

  if (socialLinks.length > 0) {
    schema.sameAs = socialLinks;
  }

  return schema;
};

/**
 * Generate WebSite structured data for homepage
 * @param {string} siteUrl - Base URL of the website
 * @returns {Object} JSON-LD WebSite schema
 */
export const generateWebSiteSchema = (siteUrl = 'https://remote-job.ai') => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Remote Job',
    description:
      'Find your dream remote job from thousands of opportunities worldwide',
    url: siteUrl,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/jobs?search={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
};

/**
 * Generate BreadcrumbList structured data
 * @param {Array} breadcrumbs - Array of breadcrumb objects with name and url
 * @returns {Object} JSON-LD BreadcrumbList schema
 *
 * @example
 * generateBreadcrumbSchema([
 *   { name: 'Home', url: 'https://remote-job.ai' },
 *   { name: 'Jobs', url: 'https://remote-job.ai/jobs' },
 *   { name: 'Job Title', url: 'https://remote-job.ai/job/123' }
 * ])
 */
export const generateBreadcrumbSchema = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  };
};
